import { Box, BoxProps, Divider, Flex, useControllableState } from '@chakra-ui/react';
import { LayoutGroup } from 'framer-motion';
import { PropsWithChildren } from 'react';

import { rem } from 'polished';
import { TabsBarItem } from './TabsBarItem';
import { TabItems } from './tabs-types';

type Props = BoxProps & {
  tabs?: TabItems;
  /** Controlled Id for default active tab. */
  defaultActiveId?: string;
  /** Controlled Id for active tab. */
  activeId?: string;
  /** onChange call when the active id changed. */
  onActiveIdChange?: (id: string) => void;
  /** Disabled state */
  isDisabled?: boolean;
  /** Test automation id */
  testId?: string;
};

export const TabsBar = ({
  tabs = [],
  isDisabled,
  activeId: controlledActiveId,
  defaultActiveId,
  onActiveIdChange,
  children,
  testId = 'tab-bar',
  ...boxProps
}: PropsWithChildren<Props>) => {
  /** Controlled & Uncontrolled support */
  const [, setActiveId] = useControllableState<string>({
    value: controlledActiveId,
    defaultValue: defaultActiveId,
    onChange: onActiveIdChange,
  });

  return (
    <Box {...boxProps}>
      <Flex justifyContent="space-between" height={{ base: rem(40), md: rem(44) }}>
        <LayoutGroup>
          <Flex position="relative" data-testid={`${testId}-list`}>
            {tabs.map((item, index, self) => (
              <Box
                key={item.label}
                mr={index < self.length - 1 ? 'space.32' : 0}
                position="relative"
              >
                <TabsBarItem
                  testId={`${testId}-item`}
                  id={item.id}
                  count={item.count}
                  isActive={item.isActive}
                  isDisabled={isDisabled || item.isDisabled}
                  onClick={() => setActiveId(item.id)}
                >
                  {item.label}
                </TabsBarItem>
              </Box>
            ))}
          </Flex>
        </LayoutGroup>
        {children}
      </Flex>
      <Divider mx="0" mb="0" mt="-1px" />
    </Box>
  );
};
